import React, { useEffect, useState, useRef } from "react";
import { generateClient } from "@aws-amplify/api";
import { Container } from "react-bootstrap";
import InputBox from "@/components/InputBox";
import HeaderSection from "@/components/HeaderSection";
import RecentSuggestedSection from "@/components/RecentSuggestedSection";
import ChatHistory from "@/components/ChatHistory";
import ChatSidebar from "@/components/ChatSidebar";
import { SYSTEM_MESSAGE } from "@/constants/systemPrompt";
import { getCompletion } from "@/graphql/queries";
import { onUpsertCompletionResponse } from "@/graphql/subscriptions";
import { gunzipBase64 } from "@/utils";
import FeedbackModal from "@/components/FeedbackModal";
import { upsertAIFeedback } from "@/graphql/mutations";

import {
  loadConvoList,
  saveConvoList,
  loadActiveId,
  saveActiveId,
  loadConvo,
  saveConvo,
  removeConvo,
} from "@/utils/chatStorage";

import "./AIAssistant.scss";

const client = generateClient();

const LEFT_SIDEBAR_WIDTH = 240;
const AI_SIDEBAR_OPEN = 320;
const AI_SIDEBAR_CLOSED = 56;

const AIAssistant = () => {
  const [recent, setRecent] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isThinking, setIsThinking] = useState(false);

  const subscriptions = useRef([]);
  const requestIdRef = useRef();
  const chatContainerRef = useRef(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedMsgIndex, setSelectedMsgIndex] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const createConversationFromMessages = (initialMessages, explicitTitle) => {
    const id = String(Date.now());
    const now = Date.now();

    const firstUserMsg = initialMessages.find(
      (m) => m.role === "user" && m.content?.[0]?.text,
    );
    const autoTitle =
      firstUserMsg?.content?.[0]?.text?.slice(0, 60) || "New chat";

    const title = explicitTitle || autoTitle;

    const convo = {
      id,
      title,
      messages: initialMessages,
      createdAt: now,
      updatedAt: now,
    };

    saveConvo(convo);

    setActiveChatId(id);
    saveActiveId(id);

    setMessages(initialMessages);

    setConversations((prev) => {
      const newList = [{ id, title, updatedAt: now }, ...prev];
      saveConvoList(newList);
      return newList;
    });
  };

  // Scroll to bottom on message change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Initial load of convo list + active convo
  useEffect(() => {
    const list = loadConvoList();
    setConversations(list);

    const activeId = loadActiveId();
    if (activeId) {
      const convo = loadConvo(activeId);
      if (convo?.messages) {
        setActiveChatId(activeId);
        setMessages(convo.messages);
      }
    }

    // Migrate legacy single-chat storage once:
    const legacy = localStorage.getItem("ai_chat_messages");
    if (legacy) {
      try {
        const msgs = JSON.parse(legacy);
        if (Array.isArray(msgs) && msgs.length) {
          const id = String(Date.now());
          const convo = {
            id,
            title: "Legacy chat",
            messages: msgs,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          saveConvo(convo);
          const newList = [
            { id, title: convo.title, updatedAt: convo.updatedAt },
            ...list,
          ];
          saveConvoList(newList);
          saveActiveId(id);
          setConversations(newList);
          setActiveChatId(id);
          setMessages(msgs);
        }
      } catch {}
      localStorage.removeItem("ai_chat_messages");
    }
  }, []);

  // Persist active convo whenever messages change
  useEffect(() => {
    if (!activeChatId) return;

    const convo = loadConvo(activeChatId) || {
      id: activeChatId,
      title: "New chat",
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    // update messages
    convo.messages = messages;

    // auto-title from first user message
    const firstUserMsg = messages.find(
      (m) => m.role === "user" && m.content?.[0]?.text,
    )?.content?.[0]?.text;
    if (
      firstUserMsg &&
      (!convo.title ||
        convo.title === "New chat" ||
        convo.title === "Legacy chat")
    ) {
      convo.title = firstUserMsg.slice(0, 40);
    }

    convo.updatedAt = Date.now();
    saveConvo(convo);

    // update list
    const list = loadConvoList();
    const idx = list.findIndex((x) => x.id === convo.id);
    const entry = {
      id: convo.id,
      title: convo.title,
      updatedAt: convo.updatedAt,
    };
    const newList =
      idx >= 0
        ? [...list.slice(0, idx), entry, ...list.slice(idx + 1)]
        : [entry, ...list];
    saveConvoList(newList);
    setConversations(newList);
  }, [messages, activeChatId]);

  // Fetch recents & suggested + subscribe to responses
  useEffect(() => {
    const fetchSuggested = async () => {
      await new Promise((res) => setTimeout(res, 300));
      setSuggested([
        {
          id: 1,
          query: "Show me my top-performing outreach methods.",
          icon: "fire",
        },
        {
          id: 2,
          query: "How many appointments are scheduled for tomorrow?",
          icon: "calendar",
        },
        {
          id: 1,
          query: "What's my conversion rate this week?",
          timestamp: "March 29, 2024",
        },
        {
          id: 2,
          query: "How many appointments are scheduled for tomorrow?",
          timestamp: "March 30, 2024",
        },
        { id: 3, query: "Call volume yesterday", timestamp: "March 30, 2024" },
        { id: 4, query: "Drop rate last month", timestamp: "March 31, 2024" },
      ]);
    };

    fetchSuggested();

    // subscription
    const sub = client
      .graphql({
        query: onUpsertCompletionResponse,
        variables: {},
      })
      .subscribe({
        next: async ({ data }) => {
          try {
            let response = data.onUpsertCompletionResponse.response;
            if (typeof response === "string") response = JSON.parse(response);
            if (
              data.onUpsertCompletionResponse.request_id !==
              requestIdRef.current
            )
              return;

            let newMsgs = response.messages;
            if (typeof newMsgs === "string") {
              try {
                newMsgs = JSON.parse(await gunzipBase64(newMsgs));
              } catch (_) {}
            }
            setMessages(newMsgs);
            setIsThinking(false);
          } catch (e) {
            console.error("Subscription parse error:", e);
            setIsThinking(false);
          }
        },
        error: (error) => {
          console.error("Subscription error:", error);
          setIsThinking(false);
        },
      });

    subscriptions.current.push(sub);
    return () => {
      subscriptions.current.forEach((e) => e.unsubscribe());
    };
  }, []);

  const sendFeedbackToBackend = async (rating, feedbackText = null) => {
    const currentRequestId = requestIdRef.current;

    if (!currentRequestId) {
      console.warn("No Request ID found. Cannot save feedback.");
      return;
    }

    try {
      const payload = {
        ai_feedbacks: {
          request_id: currentRequestId,
          rating: rating, // 'positive' أو 'negative'
          feedback_text: feedbackText,
        },
      };

      await client.graphql({
        query: upsertAIFeedback,
        variables: { input: payload },
      });

      console.log(` Feedback (${rating}) saved to DB.`);
    } catch (error) {
      console.error(" Error sending feedback:", error);
      triggerToast("Failed to save feedback. Please try again.");
    }
  };
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const userMessageText = userInput.trim();

    const userMessageObj = {
      role: "user",
      content: [
        {
          text: userMessageText,
          type: "text",
        },
      ],
    };

    let nextMessages;
    if (!activeChatId) {
      const initialMessages = [userMessageObj];
      createConversationFromMessages(initialMessages, userMessageText);
      nextMessages = initialMessages;
    } else {
      nextMessages = [...messages, userMessageObj];
      setMessages(nextMessages);
    }

    setUserInput("");
    setIsThinking(true);

    try {
      const res = await client.graphql({
        query: getCompletion,
        variables: {
          async: true,
          messages: JSON.stringify(nextMessages),
          //model: 'amazon.nova-pro-v1:0',
          //model: 'gpt-4o',
          model: "data_query_tool_agent",
          system: JSON.stringify([{ text: SYSTEM_MESSAGE }]),
        },
      });

      const { request_id } = res.data.getCompletion;
      requestIdRef.current = request_id;
    } catch (err) {
      console.error("OpenRouter Error:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: [{ text: "Failed to get response from AI.", type: "text" }],
        },
      ]);
      setIsThinking(false);
    }
  };

  const handleSuggestedClick = (query) => {
    setUserInput(query);
  };

  const handleCreateChat = () => {
    const id = String(Date.now());
    const convo = {
      id,
      title: "New chat",
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    saveConvo(convo);

    const newList = [
      { id, title: convo.title, updatedAt: convo.updatedAt },
      ...conversations,
    ];
    setConversations(newList);
    saveConvoList(newList);

    setActiveChatId(id);
    saveActiveId(id);
    setMessages([]);
  };

  const handleSelectChat = (id) => {
    const convo = loadConvo(id);
    if (convo) {
      setActiveChatId(id);
      saveActiveId(id);
      setMessages(convo.messages || []);
    }
  };

  const handleRemoveChat = (id) => {
    removeConvo(id);
    const newList = conversations.filter((c) => c.id !== id);
    setConversations(newList);
    saveConvoList(newList);

    if (activeChatId === id) {
      const nextActive = newList[0]?.id || null;
      setActiveChatId(nextActive);
      saveActiveId(nextActive);
      const nextConvo = nextActive ? loadConvo(nextActive) : null;
      setMessages(nextConvo?.messages || []);
    }
  };
  const handleRenameChat = (id, newTitle) => {
    const title = newTitle.trim();
    if (!title) return;

    const existing = loadConvo(id) || {
      id,
      title,
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    const convo = {
      ...existing,
      title,
      updatedAt: Date.now(),
    };

    saveConvo(convo);

    const list = loadConvoList();
    const idx = list.findIndex((c) => c.id === id);
    const entry = {
      id: convo.id,
      title: convo.title,
      updatedAt: convo.updatedAt,
    };

    const newList =
      idx >= 0
        ? [...list.slice(0, idx), entry, ...list.slice(idx + 1)]
        : [entry, ...list];

    saveConvoList(newList);
    setConversations(newList);
  };

  const handleFeedback = (index, type) => {
    if (type === "negative") {
      if (messages[index].feedback === "negative") {
        updateMessageFeedback(index, null, null);
      } else {
        setSelectedMsgIndex(index);
        setShowFeedbackModal(true);
      }
      return;
    }

    if (type === "positive") {
      const isNewLike = messages[index].feedback !== "positive";

      const newFeedback = isNewLike ? "positive" : null;
      updateMessageFeedback(index, newFeedback, null);

      if (isNewLike) {
        triggerToast(
          "Thank you! Your feedback helps make this assistant better.",
        );

        sendFeedbackToBackend("positive");
      }
    }
  };

  const updateMessageFeedback = (index, feedbackType, feedbackText = null) => {
    setMessages((prevMessages) => {
      const updatedMessages = prevMessages.map((msg, i) => {
        if (i === index) {
          return { ...msg, feedback: feedbackType, feedbackText: feedbackText };
        }
        return msg;
      });

      if (activeChatId) {
        const currentConvo = loadConvo(activeChatId);
        if (currentConvo) {
          currentConvo.messages = updatedMessages;
          saveConvo(currentConvo);
        }
      }

      return updatedMessages;
    });
  };

  const handleSubmitNegativeFeedback = (reason) => {
    if (selectedMsgIndex !== null) {
      updateMessageFeedback(selectedMsgIndex, "negative", reason);
      triggerToast("Thanks for the feedback. We'll look into this.");

      sendFeedbackToBackend("negative", reason);
    }
    setShowFeedbackModal(false);
  };

  const triggerToast = (message) => {
    setToastMessage(message);
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const currentSidebarOffset =
    LEFT_SIDEBAR_WIDTH + (isSidebarOpen ? AI_SIDEBAR_OPEN : AI_SIDEBAR_CLOSED);

  return (
    <div
      className="ai_assistant"
      style={{ display: "flex", minHeight: "100vh" }}
    >
      {/* AI Chat Sidebar (fixed).  */}
      <ChatSidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen((o) => !o)}
        conversations={conversations}
        activeId={activeChatId}
        onCreateChat={handleCreateChat}
        onSelectChat={handleSelectChat}
        onRemoveChat={handleRemoveChat}
        onRenameChat={handleRenameChat}
        style={{ left: `${LEFT_SIDEBAR_WIDTH}px` }}
      />

      {/* Main content area: account for both sidebars */}
      <div
        style={{
          flex: 1,
          marginLeft:
            LEFT_SIDEBAR_WIDTH +
            (isSidebarOpen ? AI_SIDEBAR_OPEN : AI_SIDEBAR_CLOSED),
          marginLeft: currentSidebarOffset,
          transition: "margin-left 180ms ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container
          className="flex-grow-1 d-flex flex-column justify-content-between"
          style={{ padding: "1.5rem" }}
        >
          <HeaderSection
            messages={messages}
            userInput={userInput}
            setUserInput={setUserInput}
            isRecording={isRecording}
            handleSendMessage={handleSendMessage}
          />

          {messages.length === 0 && (
            <RecentSuggestedSection
              recent={recent}
              suggested={suggested}
              handleSuggestedClick={handleSuggestedClick}
            />
          )}

          <ChatHistory
            messages={messages}
            isThinking={isThinking}
            chatContainerRef={chatContainerRef}
            onFeedback={handleFeedback}
          />
        </Container>

        {messages.length > 0 && (
          <InputBox
            isFixed={true}
            userInput={userInput}
            setUserInput={setUserInput}
            isRecording={isRecording}
            handleSendMessage={handleSendMessage}
            sidebarOffset={currentSidebarOffset}
          />
        )}
        <FeedbackModal
          show={showFeedbackModal}
          onHide={() => setShowFeedbackModal(false)}
          onSubmit={handleSubmitNegativeFeedback}
        />
      </div>
      {/* Toast Notification Element */}
      <div
        style={{
          position: "fixed",
          bottom: "100px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#333333",
          color: "#ffffff",
          padding: "10px 24px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          fontSize: "0.9rem",
          opacity: showToast ? 1 : 0,
          visibility: showToast ? "visible" : "hidden",
          transition: "opacity 0.3s ease, visibility 0.3s ease",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span style={{ fontSize: "1.1rem" }}>✨</span>
        {toastMessage}
      </div>
    </div>
  );
};

export default AIAssistant;
